import { useEffect, useMemo, useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

export interface MentionProfile {
  id: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
}

interface MentionTextareaProps extends Omit<React.ComponentProps<typeof Textarea>, "onChange" | "value"> {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
}

/** Extract the @usernames written inside a body of text. */
export const extractMentions = (text: string): string[] =>
  Array.from(new Set((text.match(/@([a-zA-Z0-9_.]{2,30})/g) ?? []).map((m) => m.slice(1).toLowerCase())));

/** Render text with @mentions highlighted in the accent colour. */
export const MentionText = ({ text, className }: { text: string; className?: string }) => (
  <span className={className}>
    {text.split(/(@[a-zA-Z0-9_.]{2,30})/g).map((chunk, i) =>
      chunk.startsWith("@") ? (
        <span key={i} className="text-purple-400 font-medium">{chunk}</span>
      ) : (
        <span key={i}>{chunk}</span>
      ),
    )}
  </span>
);

/**
 * Twitter-style composer: typing "@" opens a people picker that completes
 * the username inline.
 */
const MentionTextarea = ({ value, onChange, onSubmit, className, ...rest }: MentionTextareaProps) => {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const [query, setQuery] = useState<string | null>(null);
  const [results, setResults] = useState<MentionProfile[]>([]);
  const [highlight, setHighlight] = useState(0);

  const open = query !== null && results.length > 0;

  const detect = (text: string, caret: number) => {
    const before = text.slice(0, caret);
    const match = before.match(/(?:^|\s)@([a-zA-Z0-9_.]{0,30})$/);
    setQuery(match ? match[1] : null);
    setHighlight(0);
  };

  useEffect(() => {
    if (query === null) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      let q = supabase.from("profiles").select("id, username, full_name, avatar_url").limit(6);
      if (query) q = q.or(`username.ilike.%${query}%,full_name.ilike.%${query}%`);
      const { data } = await q;
      if (!cancelled) setResults((data ?? []) as MentionProfile[]);
    }, 180);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const pick = (p: MentionProfile) => {
    const el = ref.current;
    const caret = el?.selectionStart ?? value.length;
    const before = value.slice(0, caret).replace(/@([a-zA-Z0-9_.]{0,30})$/, `@${p.username} `);
    const next = before + value.slice(caret);
    onChange(next);
    setQuery(null);
    requestAnimationFrame(() => {
      el?.focus();
      const pos = before.length;
      el?.setSelectionRange(pos, pos);
    });
  };

  const hint = useMemo(() => "Type @ to mention someone", []);

  return (
    <div className="relative">
      <Textarea
        {...rest}
        ref={ref}
        value={value}
        placeholder={rest.placeholder ?? hint}
        onChange={(e) => { onChange(e.target.value); detect(e.target.value, e.target.selectionStart ?? 0); }}
        onKeyUp={(e) => detect(e.currentTarget.value, e.currentTarget.selectionStart ?? 0)}
        onClick={(e) => detect(e.currentTarget.value, e.currentTarget.selectionStart ?? 0)}
        onKeyDown={(e) => {
          if (open) {
            if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => (h + 1) % results.length); return; }
            if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => (h - 1 + results.length) % results.length); return; }
            if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(results[highlight]); return; }
            if (e.key === "Escape") { setQuery(null); return; }
          }
          if (onSubmit && e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSubmit(); }
        }}
        className={cn("bg-transparent", className)}
      />

      {open && (
        <div className="absolute z-50 bottom-full mb-2 left-0 w-72 rounded-xl border border-border bg-popover shadow-2xl overflow-hidden">
          {results.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(p); }}
              className={cn(
                "w-full flex items-center gap-2 px-3 py-2 text-left transition-colors",
                i === highlight ? "bg-muted" : "hover:bg-muted/60",
              )}
            >
              <Avatar className="h-7 w-7">
                <AvatarImage src={p.avatar_url ?? undefined} alt={p.username} />
                <AvatarFallback>{(p.username ?? "?").slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <span className="min-w-0">
                <span className="block text-sm font-medium truncate">{p.full_name || p.username}</span>
                <span className="block text-xs text-muted-foreground truncate">@{p.username}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionTextarea;
